<template>

  <v-container fluid class="route-driving-directions px-0 pt-0" v-if="route">

    <!------------------------------------------------->
    <!-- Driving Directions as Text (lagacy support) -->
    <!------------------------------------------------->
    <template v-if="drivingDirectionsText">
      <v-row class="mb-3">
        <v-col cols="12" class="pl-0">
          <div class="text-h5">Directions</div>
        </v-col>
      </v-row>
      <v-row class="mb-2 pt-7 pb-7 px-8 page-content">
        <v-col cols="12" class="pa-0 ma-0">
          <div>{{ drivingDirectionsText }}</div>
        </v-col>
      </v-row>
    </template>

    <!----------------------------------------->
    <!-- Driving Directions Detailed Display -->
    <!----------------------------------------->
    <template v-else>

      <!------------------->
      <!-- Claim Vehicle -->
      <!------------------->
      <v-expansion-panels flat accordion class="mt-4" v-model="expandClaimVehicle">
        <v-expansion-panel>
          <v-expansion-panel-header>
            <div class="step-title">
              Claim Vehicle
            </div>
          </v-expansion-panel-header>
          <v-expansion-panel-content class="pl-2">
            <v-row class="mt-4 pl-3">
              <v-col cols="12" xs="12" sm="3" md="2" lg="2" class="pa-0 ma-0">
                <div class="attr-label">Start from:</div>
              </v-col>
              <v-col cols="12" xs="12" sm="9" md="10" lg="10" class="pa-0 ma-0">
                <div class="attr-value" v-if="vehicleLot">
                  <strong>{{ vehicleLot.name }}</strong>
                </div>
              </v-col>
            </v-row>
            <v-row class="mt-8 pl-3">
              <v-col cols="12" xs="12" sm="3" md="2" lg="2" class="pa-0 ma-0">
                <div class="attr-label">Address:</div>
              </v-col>
              <v-col cols="12" xs="12" sm="9" md="10" lg="10" class="pa-0 ma-0">
                <template v-if="vehicleLot">
                  <div class="attr-value">{{ vehicleLotMultilineAddress.street_1 }}</div>
                  <div class="attr-value" v-if="vehicleLotMultilineAddress.street_2">{{ vehicleLotMultilineAddress.street_2 }}</div>
                  <div class="attr-value" v-if="vehicleLotMultilineAddress.area">{{ vehicleLotMultilineAddress.area }}</div>
                </template>
              </v-col>
            </v-row>
            <v-row class="mt-8 pl-3">
              <v-col cols="12" xs="12" sm="3" md="2" lg="2" class="pa-0 ma-0">
                <div class="attr-label">Start at:</div>
              </v-col>
              <v-col cols="12" xs="12" sm="9" md="10" lg="10" class="pa-0 ma-0">
                <div class="attr-value">{{ formatDateTime(route.scheduled_start_time) }}</div>
              </v-col>
            </v-row>
            <v-row class="mt-8 pl-3 pb-4">
              <v-col cols="12" xs="12" sm="3" md="2" lg="2" class="pa-0 ma-0">
                <div class="attr-label">Vehicle:</div>
              </v-col>
              <v-col cols="12" xs="12" sm="9" md="10" lg="10" class="pa-0 ma-0">
                <div class="attr-value">{{ vehicle.license_plate }}</div>
              </v-col>
            </v-row>
          </v-expansion-panel-content>
        </v-expansion-panel>
      </v-expansion-panels>

      <!-------------------->
      <!-- Delivery Steps -->
      <!-------------------->
      <v-expansion-panels
          v-for="(step, stepIndex) in deliverySteps"
          flat
          accordion
          class="mt-4"
          :key="`delivery-step-${stepIndex}`"
          v-model="step.is_expanded">
        <v-expansion-panel>
          <v-expansion-panel-header>
            <template v-if="step.type === 'pickup'">
              <div class="step-title d-flex align-center" v-if="step.order_numbers.length > 1">
                <span class="step-number pr-2">({{ stepIndex + 1 }})</span> Pickup Orders: <span class="ml-2 order-number">{{ step.order_numbers.join(', ') }}</span>
              </div>
              <div class="step-title d-flex align-center" v-else>
                <span class="step-number pr-2">({{ stepIndex + 1 }})</span> Pickup Order: <span class="ml-2 order-number">{{ step.order_numbers.join(', ') }}</span>
              </div>
            </template>
            <template v-else-if="step.type === 'dropoff'">
              <div class="step-title d-flex align-center" v-if="step.order_numbers.length > 1">
                <span class="step-number pr-2">({{ stepIndex + 1 }})</span> Deliver Orders: <span class="ml-2 order-number">{{ step.order_numbers.join(', ') }}</span>
              </div>
              <div class="step-title d-flex align-center" v-else>
                <span class="step-number pr-2">({{ stepIndex + 1 }})</span> Deliver Order: <span class="ml-2 order-number">{{ step.order_numbers.join(', ') }}</span>
              </div>
            </template>
          </v-expansion-panel-header>
          <v-expansion-panel-content class="pt-8 pl-2">
            <v-row class="pl-3">
              <v-col cols="12" xs="12" sm="3" md="2" lg="2" class="pa-0 ma-0">
                <div class="attr-label">Destination:</div>
              </v-col>
              <v-col cols="12" xs="12" sm="9" md="10" lg="10" class="pa-0 ma-0">
                <div class="attr-value" v-if="vehicleLot">
                  <strong>{{ step.location_name }}</strong>
                </div>
              </v-col>
            </v-row>

            <v-row class="mt-8 pl-3">
              <v-col cols="12" xs="12" sm="3" md="2" lg="2" class="pa-0 ma-0">
                <div class="attr-label">Address:</div>
              </v-col>
              <v-col cols="12" xs="12" sm="9" md="10" lg="10" class="pa-0 ma-0">
                <template v-if="step.type === 'pickup'">
                  <div class="attr-value">{{ getShipperMultilineAddressForShipper(step.order_numbers[0]).street_1 }}</div>
                  <div class="attr-value" v-if="getShipperMultilineAddressForShipper(step.order_numbers[0]).street_2">{{ getShipperMultilineAddressForShipper(step.order_numbers[0]).street_2 }}</div>
                  <div class="attr-value" v-if="getShipperMultilineAddressForShipper(step.order_numbers[0]).area">{{ getShipperMultilineAddressForShipper(step.order_numbers[0]).area }}</div>
                </template>
                <template v-else-if="step.type === 'dropoff'">
                  <div class="attr-value">{{ getShipperMultilineAddressForReceiver(step.order_numbers[0]).street_1 }}</div>
                  <div class="attr-value" v-if="getShipperMultilineAddressForReceiver(step.order_numbers[0]).street_2">{{ getShipperMultilineAddressForReceiver(step.order_numbers[0]).street_2 }}</div>
                  <div class="attr-value" v-if="getShipperMultilineAddressForReceiver(step.order_numbers[0]).area">{{ getShipperMultilineAddressForReceiver(step.order_numbers[0]).area }}</div>
                </template>
              </v-col>
            </v-row>

            <v-row class="mt-8 pl-3">
              <v-col cols="12" xs="12" sm="3" md="2" lg="2" class="pa-0 ma-0">
                <div class="attr-label">Est. arrival:</div>
              </v-col>
              <v-col cols="12" xs="12" sm="9" md="10" lg="10" class="pa-0 ma-0">
                <div class="attr-value">{{ formatDateTime(step.est_arrival) }}</div>
              </v-col>
            </v-row>

            <v-row class="mt-8 pl-3">
              <v-col cols="12" xs="12" sm="3" md="2" lg="2" class="pa-0 ma-0">
                <div class="attr-label">Est. departure:</div>
              </v-col>
              <v-col cols="12" xs="12" sm="9" md="10" lg="10" class="pa-0 ma-0">
                <div class="attr-value">{{ formatDateTime(step.est_departure) }}</div>
              </v-col>
            </v-row>

            <v-row class="mt-8 mb-4 pl-3">
              <v-col cols="12" xs="12" sm="3" md="2" lg="2" class="pa-0 ma-0">
                <div class="attr-label">Directions:</div>
              </v-col>
              <v-col cols="12" xs="12" sm="9" md="10" lg="10" class="pa-0 ma-0">
                <div class="mb-2 attr-value" v-for="(instruction, index) in step.directions" :key="`instruction-${index}`">
                  {{ instruction }}
                </div>
              </v-col>
            </v-row>

            <!-- Inline Driver Notes -->
            <template v-if="step.type === 'dropoff'">
              <template v-for="orderNum in step.order_numbers">
                <v-row class="mt-6 mb-2 pl-3 d-flex align-center" :key="`driver-note-${orderNum}`" v-if="getDriverNotesForOrder(orderNum)">
                  <v-col cols="12" xs="12" sm="4" md="2" lg="2" class="pa-0 ma-0">
                    <div class="d-flex align-center">
                      <v-icon style="font-size:18px;" color="amber darken-1">mdi-note-outline</v-icon>
                      <span style="color:#ffb300;" class="ml-2">{{ orderNum }}:</span>
                    </div>
                  </v-col>
                  <v-col cols="12" xs="12" sm="8" md="10" lg="10" class="pa-0 ma-0">
                    <div class="attr-value">
                      {{ getDriverNotesForOrder(orderNum) }}
                    </div>
                  </v-col>
                </v-row>
              </template>
            </template>

            <!-- Route Change Notes -->
            <template v-if="getRouteChangeForStep(stepIndex)">
                <v-row class="mt-9 mb-2 pl-3 d-flex align-center">
                  <v-col cols="12" xs="12" sm="4" md="2" lg="2" class="pa-0 ma-0 align-self-start">
                    <div class="route-change-label d-flex align-center">
                      <v-icon style="font-size:18px;" color="#ebfe00">mdi-directions-fork</v-icon>
                      <span class="ml-1">Route change</span>
                    </div>
                  </v-col>
                  <v-col cols="12" xs="12" sm="8" md="10" lg="10" class="pa-0 ma-0 d-flex align-center">
                    <div class="d-flex flex-column">
                      <div class="d-flex align-center geolocation-coords">
                        <v-icon style="font-size:22px;" class="mr-2">mdi-map-marker</v-icon>
                        <span class="attr-value mr-3">
                          lat: {{ getRouteChangeForStep(stepIndex).loc.coords.latitude }},
                        </span>
                        <span class="attr-value">
                          lon: {{ getRouteChangeForStep(stepIndex).loc.coords.longitude }}
                        </span>
                      </div>
                      <div class="mt-2 ml-2 geolocation-info-text">GPS coordinates of driver when route change occurred</div>
                    </div>
                  </v-col>
                </v-row>
                <v-row class="mt-6 mb-2 pl-3 d-flex align-center">
                  <v-col cols="12" xs="12" sm="4" md="2" lg="2" class="pa-0 ma-0">
                  </v-col>
                  <v-col cols="12" xs="12" sm="8" md="10" lg="10" class="pa-0 ma-0">
                    <div class="route-change-info-text">Details of change:</div>
                    <div class="attr-value">
                      {{ getRouteChangeForStep(stepIndex).notes }}
                    </div>
                  </v-col>
                </v-row>
            </template>
          </v-expansion-panel-content>
        </v-expansion-panel>
      </v-expansion-panels>

      <!-------------------->
      <!-- Return Vehicle -->
      <!-------------------->
      <v-expansion-panels flat accordion class="mt-4" v-model="expandReturnVehicle">
        <v-expansion-panel>
          <v-expansion-panel-header>
            <div class="step-title">
              Return Vehicle
            </div>
          </v-expansion-panel-header>
          <v-expansion-panel-content class="pl-2">
            <v-row class="mt-4 pl-3">
              <v-col cols="12" xs="12" sm="3" md="2" lg="2" class="pa-0 ma-0">
                <div class="attr-label">Destination:</div>
              </v-col>
              <v-col cols="12" xs="12" sm="9" md="10" lg="10" class="pa-0 ma-0">
                <div class="attr-value" v-if="vehicleLot">
                  <strong>{{ vehicleLot.name }}</strong>
                </div>
              </v-col>
            </v-row>
            <v-row class="mt-8 pl-3">
              <v-col cols="12" xs="12" sm="3" md="2" lg="2" class="pa-0 ma-0">
                <div class="attr-label">Address:</div>
              </v-col>
              <v-col cols="12" xs="12" sm="9" md="10" lg="10" class="pa-0 ma-0">
                <template v-if="vehicleLot">
                  <div class="attr-value">{{ vehicleLotMultilineAddress.street_1 }}</div>
                  <div class="attr-value" v-if="vehicleLotMultilineAddress.street_2">{{ vehicleLotMultilineAddress.street_2 }}</div>
                  <div class="attr-value" v-if="vehicleLotMultilineAddress.area">{{ vehicleLotMultilineAddress.area }}</div>
                </template>
              </v-col>
            </v-row>
            <v-row class="mt-8 pl-3">
              <v-col cols="12" xs="12" sm="3" md="2" lg="2" class="pa-0 ma-0">
                <div class="attr-label">Est. arrival:</div>
              </v-col>
              <v-col cols="12" xs="12" sm="9" md="10" lg="10" class="pa-0 ma-0">
                <div class="attr-value">{{ formatDateTime(returnStep.est_arrival) }}</div>
              </v-col>
            </v-row>
            <v-row class="mt-8 mb-2 pl-3">
              <v-col cols="12" xs="12" sm="3" md="2" lg="2" class="pa-0 ma-0">
                <div class="attr-label">Directions:</div>
              </v-col>
              <v-col cols="12" xs="12" sm="9" md="10" lg="10" class="pa-0 ma-0">
                <div class="mb-2 attr-value" v-for="(instruction, index) in returnStep.return_directions" :key="`return-instruction-${index}`">
                  {{ instruction }}
                </div>
              </v-col>
            </v-row>
          </v-expansion-panel-content>
        </v-expansion-panel>
      </v-expansion-panels>

    </template>

    <!----------------------------------->
    <!-- Driver Notes (legacy support) -->
    <!----------------------------------->
    <template v-if="drivingDirectionsText">
      <v-row class="mt-6 mb-3">
        <v-col cols="12" class="pl-0">
          <div class="text-h5">Driver Notes</div>
        </v-col>
      </v-row>

      <v-row class="mt-0 pt-2 pb-3 px-6 page-content">
        <v-col xs="12" class="pl-0 pt-0">
          <div>
            <template v-if="driverNotes">
              <div v-for="(note, index) in driverNotes" :key="`note-${index}`" class="mt-4">
                {{ note }}
              </div>
            </template>
            <template v-else>
              <div class="mt-4">(none)</div>
            </template>
          </div>
        </v-col>
      </v-row>
    </template>

  </v-container>

</template>

<script>
export default {
  name: 'RouteDrivingDirections',

  props: {
    route: {
      type: Object,
      default: null
    }
  },

  data () {
    return {
      expandClaimVehicle: false,
      expandReturnVehicle: false
    }
  },

  computed: {
    // The orders of the Route (sorted by estimated receiver arrival time)
    orders () {
      return (this.route.orders)
        ? this.$root.utils.orderBy(this.route.orders, ['estimated_receiver_arrival_time'], ['asc'])
        : []
    },
    // The full driving directions as text (this is legacy)
    drivingDirectionsText () {
      return this.routeInfo.driving_directions
    },
    // The driver notes from the Order objects (the new architecture)
    driverNotesFromOrders () {
      return this.orders
        .filter(order => order.driver_notes?.dropoff)
        .map(order => `${order.order_number} - ${order.driver_notes.dropoff}`)
    },
    // Backwards-compatible driver notes for display (for legacy driving directions)
    driverNotes () {
      return (this.driverNotesFromOrders.length > 0)
        ? this.driverNotesFromOrders
        : (this.route.driver_notes && !this.$root.utils.isEmpty(this.route.driver_notes)) ? this.route.driver_notes : null
    },
    // The route info details
    routeInfo () {
      return this.route.route_info || {}
    },
    // Route changes
    routeChanges () {
      return this.route.route_info?.route_changes || []
    },
    // The vehicle lot (start/end of route)
    vehicleLot () {
      return this.route.vehicle_lot || {}
    },
    // The vehicle lot address
    vehicleLotMultilineAddress () {
      return this.$root.utils.getAddressTextFromData(this.vehicleLot, { multi_lines: true })
    },
    // The assigned vehicle
    vehicle () {
      return this.route.vehicle || {}
    },
    returnStep () {
      return this.routeInfo.hq || {}
    },
    deliverySteps () {
      return this.routeInfo.steps || []
    }
  },

  methods: {
    // Display a date/time
    formatDateTime (date) {
      return (date) ? this.$root.utils.dateFormat(date, 'h:mma z - MMM D, YYYY') : ''
    },

    getDriverNotesForOrder (orderNumber) {
      const order = this.orders.find(order => order.order_number === orderNumber)
      return order?.driver_notes?.dropoff
    },

    getRouteChangeForStep (stepIndex) {
      return (stepIndex + 1 > 0)
        ? this.routeChanges.find(it => it.route_step_index === stepIndex + 1)
        : null
    },

    getShipperMultilineAddressForShipper (orderNumber) {
      return this.getMultilineAddress(this.orders.find(order => order.order_number === orderNumber).shipper)
    },

    getShipperMultilineAddressForReceiver (orderNumber) {
      return this.getMultilineAddress(this.orders.find(order => order.order_number === orderNumber).receiver)
    },

    // Get a multiline address for the provided data object
    getMultilineAddress (data = {}) {
      return this.$root.utils.getAddressTextFromData(data, { multi_lines: true })
    }
  }
}
</script>

<style scoped>
  .route-driving-directions .step-title {
    font-weight: 700;
    font-size: 1.15rem;
  }

  .route-driving-directions .attr-label {
    color: #9c9c9c;
    font-weight: 400;
    font-style: italic;
  }

  .route-driving-directions .route-change-label {
    color: #d9d9d9;
    font-weight: 400;
    border: 1px solid #d9d9d9;
    border-radius: 5px;
    width: 136px;
    padding: 4px 0 4px 5px;
  }

  .route-driving-directions .order-number {
    color: #F8A824;
    font-size: 1.0rem;
  }

  .route-driving-directions .step-number {
    font-size: 15px;
    padding-bottom: 1px;
  }

  .route-driving-directions .geolocation-info-text {
    color: #9c9c9c;
    font-size: 0.94rem;
    font-style: italic;
  }

  .route-driving-directions .route-change-info-text {
    color: #9c9c9c;
    font-size: 1.0rem;
  }

  .route-driving-directions .theme--dark.v-expansion-panels .v-expansion-panel {
    background-color: #161616;
    border: 1px solid #262626;
  }

  .route-driving-directions .v-expansion-panel--active>.v-expansion-panel-header {
    min-height: unset;
  }

  .route-driving-directions .v-expansion-panel-content__wrap {
    padding-left: 0px;
  }
</style>
